import type { ReactNode } from "react";
import { cn } from "../../utils/cn";

interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  /** Tooltip side — right for table/row indicators, top for buttons. */
  side?: "top" | "right" | "bottom";
  className?: string;
  /** Useful when the trigger is a disabled element that has no hover events. */
  wrapped?: boolean;
}

/**
 * Minimal CSS tooltip. Use for small informative hints (e.g. "already in 2 other
 * groups", or why an activate button is disabled). Not for long-form content.
 */
export function Tooltip({ content, children, side = "top", className }: TooltipProps) {
  const sideClasses: Record<string, string> = {
    top: "bottom-full left-1/2 mb-1.5 -translate-x-1/2",
    right: "left-full top-1/2 ml-1.5 -translate-y-1/2",
    bottom: "top-full left-1/2 mt-1.5 -translate-x-1/2",
  };
  return (
    <span className={cn("group/tip relative inline-flex", className)}>
      {children}
      <span
        role="tooltip"
        className={cn(
          "pointer-events-none absolute z-50 max-w-xs whitespace-normal rounded-md border border-background-200 bg-foreground-950 px-2.5 py-1.5 text-xs font-normal normal-case text-background-50 opacity-0 shadow-lg shadow-foreground-950/20 transition-opacity duration-150 group-hover/tip:opacity-100",
          sideClasses[side]
        )}
      >
        {content}
      </span>
    </span>
  );
}